/**
 * Microcourse Studio — SlideCanvas
 * 渲染单张 slide 的所有 elements，支持 fragment 逐步显示
 */

import React from 'react'
import type { MicroSlide, MicroElement } from '@ai-tutor/shared'
import { ElementRenderer } from './elements/ElementRenderer'
import type { QuizSubmitResult } from './elements/ElementRenderer'
import { fragmentElementVisibleCount } from './playback/useAutoplay'

export interface SlideCanvasProps {
  slide: MicroSlide
  /** 当前 fragment index，-1 表示全部显示 */
  fragmentIndex?: number
  /** 是否高亮选中状态（编辑模式） */
  isSelected?: boolean
  /** 点击 slide 回调 */
  onClick?: () => void
  /** 自定义 className */
  className?: string
  /** 是否处于编辑模式（白板等可交互元素可编辑） */
  editable?: boolean
  /** 元素内容变更回调（编辑模式下） */
  onElementChange?: (element: MicroElement) => void
  /** quiz 提交回调（用于错题同步） */
  onQuizResult?: (result: QuizSubmitResult) => void
}

export function SlideCanvas({
  slide,
  fragmentIndex = -1,
  isSelected = false,
  onClick,
  className = '',
  editable = false,
  onElementChange,
  onQuizResult,
}: SlideCanvasProps) {
  // 当前 fragment 累计应显示的元素数量（与 TTS 朗读内容同步）
  const visibleCount = fragmentElementVisibleCount(slide, fragmentIndex)

  return (
    <div
      onClick={onClick}
      className={`
        relative w-full h-full flex flex-col justify-center
        px-8 py-6 overflow-y-auto
        ${isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''}
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}
    >
      {/* Slide title (if not already a heading element) */}
      {slide.elements.length > 0 &&
        slide.elements[0].type !== 'heading' &&
        slide.title && (
          <h2 className="text-xl font-bold mb-4 text-slate-800">{slide.title}</h2>
        )}

      {/* Elements */}
      <div className="space-y-1">
        {slide.elements.map((element, idx) => (
          <ElementRenderer
            key={element.id}
            element={element}
            fragmentIndex={fragmentIndex}
            elementFragmentIndex={idx}
            elementVisibleCount={visibleCount}
            editable={editable}
            onElementChange={onElementChange}
            onQuizResult={onQuizResult}
          />
        ))}
      </div>

      {/* Empty state */}
      {slide.elements.length === 0 && (
        <div className="flex items-center justify-center h-full text-slate-300 text-sm">
          空白幻灯片
        </div>
      )}
    </div>
  )
}
